import { writable } from 'svelte/store';

/**
 * Holds a key identifying the page's hero image — the first image block in
 * document order, which is the Largest Contentful Paint (LCP) candidate.
 *
 * The matching `ImgixImage` renders eagerly with `fetchpriority="high"`, while
 * every other image stays `loading="lazy"`. Previously this was decided by a
 * "first `ImgixImage` to instantiate" heuristic, which depended on component
 * creation order and frequently tagged the wrong image (e.g. a smaller image
 * higher in the tree), leaving the real LCP image lazy-loaded and unprioritised.
 *
 * Resolving the hero from the page data instead makes the decision deterministic
 * and — crucially — keeps it in sync with funnel-publisher's hero-image preload
 * tag (see `getHeroImageSrc`), so the server preload and the client `<img>`
 * reference the same resource.
 */
export const heroImageKey = writable<string | null>(null);

type ComponentNode = {
    _id?: string;
    componentType?: string;
    content?: { src?: string; srcType?: string };
    components?: ComponentNode[];
};

/**
 * Query params that must not take part in the image's identity, because a render
 * path removes them before the `src` reaches the `<img>`.
 *
 * `Answer.svelte` runs the media-question answer `src` through
 * `deleteIrrelevantImgixKeys`, which deletes exactly these keys. Every other
 * hero-candidate path (`Image.Controller`, `List.Controller` → `ListItem`) passes
 * `content.src` through untouched, so dropping the same keys on both sides of the
 * comparison keeps it symmetric regardless of which path renders the image.
 */
const IGNORED_QUERY_PARAMS = ['ixid', 'ixlib', 'q', 'w'];

/**
 * Reduce an image URL to a stable identity for hero matching: `origin + pathname`
 * plus the query params that affect how the image is rendered, normalised.
 *
 * The hero is matched by URL rather than by component `_id` because the `_id`
 * never reaches most `Image.View` render paths: a media question, for example,
 * flattens its answers in `MediaQuestion.Controller` and passes only the nested
 * media component's `src` down (the `id` prop there is the *answer* id, used for
 * click tracking). Matching on the URL works for every render path without
 * threading a new prop through each one, and it is also what funnel-publisher
 * preloads.
 *
 * Params in `IGNORED_QUERY_PARAMS` are dropped; the rest are kept, so two crops of
 * the same source image (differing in `crop`, `fit`, `ar`, `fp-x`, `h`, ...) get
 * distinct keys and only the real hero is prioritised. Remaining params are sorted
 * and compared decoded, which makes the key insensitive to param order and to the
 * re-encoding `deleteIrrelevantImgixKeys` performs via `qs.stringify` (it writes
 * `auto=compress%2Cformat` where the page data has `auto=compress,format`).
 */
export const toImageKey = (src: string | undefined | null): string | null => {
    if (!src) {
        return null;
    }

    try {
        const { origin, pathname, searchParams } = new URL(src);

        IGNORED_QUERY_PARAMS.forEach((param) => searchParams.delete(param));

        const canonicalQuery = [...searchParams]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value]) => `${key}=${value}`)
            .join('&');

        return canonicalQuery ? `${origin}${pathname}?${canonicalQuery}` : origin + pathname;
    } catch {
        // Relative or malformed URL — fall back to the raw value so equal strings
        // still match each other.
        return src;
    }
};

/**
 * An image block is a `media` component with an image `src` (i.e. not a video).
 * Mirrors the check in funnel-publisher's `heroImage.ts`.
 */
const isImageComponent = (component: ComponentNode): boolean => {
    if (component.componentType !== 'media') {
        return false;
    }

    const content = component.content;

    return Boolean(content?.src) && content?.srcType !== 'video';
};

/**
 * Find the first image component in document order.
 *
 * The traversal is a full depth-first pre-order walk, which is what makes the
 * result match the rendered DOM order. It mirrors funnel-publisher's
 * `getPageImagesInDocumentOrder`, so the client's priority `<img>` and the
 * server's `<link rel="preload">` hero tag always resolve to the same image.
 *
 * An earlier version only inspected top-level components and the
 * `gridRow -> gridColumn -> media` shape, so it silently skipped images nested
 * any deeper. A media question nests them four levels down
 * (`gridRow -> gridColumn -> questionMedia -> questionMediaAnswer -> media`),
 * so on those pages the walk ran past the real above-the-fold images and tagged
 * a `media` block far below the fold instead — leaving the visible images lazy
 * and spending `fetchpriority="high"` on something offscreen.
 */
const findFirstImageComponent = (components: ComponentNode[] | undefined): ComponentNode | null => {
    if (!Array.isArray(components)) {
        return null;
    }

    for (const component of components) {
        if (isImageComponent(component)) {
            return component;
        }

        const nested = findFirstImageComponent(component.components);

        if (nested) {
            return nested;
        }
    }

    return null;
};

/**
 * Resolve the hero image key for a page's component tree.
 *
 * Returns `null` when no eligible image is found (no image is prioritised),
 * which matches the publisher emitting no hero preload for the same page.
 */
export const resolveHeroImageKey = (components: ComponentNode[] | undefined): string | null =>
    toImageKey(findFirstImageComponent(components)?.content?.src);
